import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ArrowLeft, CheckCircle2, Download, FileText, RefreshCw, Save } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { ComplianceTopology, CsddControl, projectsApi, type ProjectComplianceProfile } from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'
import { useTranslation } from 'react-i18next'

interface Props {
  complianceProfiles?: ProjectComplianceProfile[]
  projectId: string
  canEdit: boolean
  onBack: () => void
  onOpenZones: () => void
}

export const CsddWorkspace: React.FC<Props> = ({ projectId, canEdit, onBack, onOpenZones, complianceProfiles = [] }) => {
  const { t } = useTranslation()
  const [selectedProfileCode, setSelectedProfileCode] = useState('')
  const profileCode = complianceProfiles.find(profile => profile.profile_code === selectedProfileCode)?.profile_code
    ?? complianceProfiles[0]?.profile_code
  const { error: showError } = useToast()
  const [data, setData] = useState<ComplianceTopology>({ nodes: [], links: [], conduits: [], controls: [], issues: [] })
  const [selectedNode, setSelectedNode] = useState('')
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const next = await projectsApi.getComplianceTopology(projectId)
      setData(next)
      setSelectedNode(current => current || next.nodes.find(node => node.is_target)?.id || '')
    } catch (error: any) {
      showError(error?.message ?? t('csdd.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [projectId, showError, t])

  useEffect(() => { void load() }, [load])

  const targets = data.nodes.filter(node => node.is_target)
  const selected = targets.find(node => node.id === selectedNode)
  const controls = useMemo(
    () => data.controls.filter(control => control.topology_node_id === selectedNode),
    [data.controls, selectedNode],
  )
  const unresolved = data.controls.filter(control => control.status === 'tbu').length

  if (loading && !data.nodes.length) {
    return <div className="flex items-center gap-2 p-6 text-sm text-slate-500"><RefreshCw className="h-4 w-4 animate-spin" />{t('common.loading')}</div>
  }

  if (!targets.length) {
    return (
      <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-5 dark:border-amber-900 dark:bg-amber-950/20">
        <p className="text-sm text-amber-800 dark:text-amber-200">{t('csdd.needsTopology')}</p>
        <Button size="sm" onClick={onOpenZones}>{t('csdd.openZones')}</Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400">
          <ArrowLeft className="h-4 w-4" /> {t('projectDetail.backToCompliance')}
        </button>
        <div className="flex flex-wrap items-center gap-2">
          {complianceProfiles.length > 0 && (
            <label className="flex items-center gap-2 text-xs text-slate-500">
              {t('raGeneration.classProfile')}
              <select aria-label={t('raGeneration.classProfile')} value={profileCode} disabled={downloading} onChange={event => setSelectedProfileCode(event.target.value)} className="rounded border border-slate-300 bg-white px-2 py-1 dark:border-slate-600 dark:bg-slate-900">
                {complianceProfiles.map(profile => <option key={profile.profile_code} value={profile.profile_code}>{profile.class_society} · {profile.edition_label}</option>)}
              </select>
            </label>
          )}
          <Button variant="outline" size="sm" onClick={onOpenZones}>{t('csdd.openZones')}</Button>
          <Button
            size="sm"
            disabled={downloading}
            onClick={async () => {
              setDownloading(true)
              try {
                await projectsApi.downloadComplianceDocument(projectId, 'csdd', `Cyber_Security_Design_Description${profileCode ? `_${profileCode}` : ''}.docx`, '0', profileCode)
              } catch (error: any) {
                showError(error?.message ?? t('csdd.generateFailed'))
              } finally {
                setDownloading(false)
              }
            }}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" /> {t('csdd.generateDocx')}
          </Button>
        </div>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900 dark:text-white">
              <FileText className="h-5 w-5 text-blue-500" /> {t('csdd.title')}
            </h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t('csdd.description')}</p>
          </div>
          <span className={`rounded px-2 py-1 text-xs font-medium ${unresolved ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
            {unresolved ? t('csdd.tbuCount', { count: unresolved }) : t('csdd.ready')}
          </span>
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800">
          <h3 className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{t('csdd.targetSystems')}</h3>
          <div className="mt-2 space-y-1">
            {targets.map(node => {
              const nodeControls = data.controls.filter(control => control.topology_node_id === node.id)
              const pending = nodeControls.filter(control => control.status === 'tbu').length
              return (
                <button key={node.id} type="button" onClick={() => setSelectedNode(node.id)} className={`flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-xs ${selectedNode === node.id ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700'}`}>
                  <span className="truncate font-medium">{node.system_name}</span>
                  {pending ? <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700">{pending}</span> : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />}
                </button>
              )
            })}
          </div>
        </aside>

        <main className="space-y-3">
          {selected && (
            <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
              <h3 className="font-semibold text-slate-900 dark:text-white">{selected.system_name}</h3>
              <p className="mt-1 text-xs text-slate-500">{selected.ship_function || 'TBU'} · {selected.supplier || 'TBU'} · {selected.location || 'TBU'}</p>
              <p className="mt-2 text-xs text-slate-500">{t('csdd.identifyReference')}</p>
            </section>
          )}
          <div className="grid gap-3 xl:grid-cols-2">
            {controls.map(control => (
              <ControlEditor
                key={control.id}
                control={control}
                canEdit={canEdit}
                onSaved={next => setData(current => ({
                  ...current,
                  controls: current.controls.map(item => item.id === next.id ? next : item),
                  issues: current.issues.filter(issue => issue.id !== next.id),
                }))}
                projectId={projectId}
              />
            ))}
          </div>
          {controls.some(control => control.status === 'tbu') && (
            <p className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300">
              <AlertTriangle className="h-3.5 w-3.5" /> {t('csdd.tbuNotice')}
            </p>
          )}
        </main>
      </div>
    </div>
  )
}

function ControlEditor({ control, canEdit, projectId, onSaved }: {
  control: CsddControl
  canEdit: boolean
  projectId: string
  onSaved: (control: CsddControl) => void
}) {
  const { t } = useTranslation()
  const { error: showError } = useToast()
  const [draft, setDraft] = useState(control)
  const [saving, setSaving] = useState(false)
  useEffect(() => setDraft(control), [control])

  const save = async () => {
    setSaving(true)
    try {
      const data = await projectsApi.updateCsddControl(projectId, control.id, {
        status: draft.status,
        narrative: draft.narrative || 'TBU',
        reference: draft.reference,
        confirmed: draft.status !== 'tbu',
      })
      const saved = data.controls.find(item => item.id === control.id)
      if (saved) onSaved(saved)
    } catch (error: any) {
      showError(error?.message ?? t('common.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className={`rounded-xl border bg-white p-3 dark:bg-slate-800 ${draft.status === 'tbu' ? 'border-amber-300 dark:border-amber-800' : 'border-emerald-200 dark:border-emerald-900'}`}>
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">{t(`csdd.requirements.${control.requirement_code}`)}</h4>
        <select value={draft.status} disabled={!canEdit} onChange={event => setDraft(value => ({ ...value, status: event.target.value as CsddControl['status'] }))} className="rounded border border-slate-200 bg-white px-1.5 py-1 text-[10px] dark:border-slate-600 dark:bg-slate-900">
          <option value="tbu">TBU</option>
          <option value="complete">{t('csdd.complete')}</option>
          <option value="not_applicable">N/A</option>
        </select>
      </div>
      <textarea value={draft.narrative} readOnly={!canEdit} onChange={event => setDraft(value => ({ ...value, narrative: event.target.value }))} rows={5} className="mt-2 w-full rounded border border-slate-200 px-2 py-1.5 text-xs leading-relaxed text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200" />
      <input value={draft.reference ?? ''} readOnly={!canEdit} onChange={event => setDraft(value => ({ ...value, reference: event.target.value }))} placeholder={t('csdd.reference')} className="mt-2 w-full rounded border border-slate-200 px-2 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-900" />
      {canEdit && (
        <Button size="sm" className="mt-2 h-7 px-2 text-xs" disabled={saving} onClick={() => void save()}>
          <Save className="mr-1 h-3.5 w-3.5" /> {t('common.save')}
        </Button>
      )}
    </section>
  )
}
